// Analítica y estadísticas del catálogo (Tarea 19F, Req. 22.1–22.3, 22.7–22.10).
//
// Constructor de vistas: seleccionar una o varias DIMENSIONES de agrupación,
// MÉTRICAS y FILTROS (con rangos); ejecuta la consulta y muestra una TABLA
// ordenable y un GRÁFICO simple de barras. Permite ordenar, limitar top N y
// exportar el resultado a CSV. Usable en móvil.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import type { AnalyticsQueryBody, SearchCriteria } from '../api/endpoints.js';
import { AlertMessage, AsyncBoundary, Card, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import type { AnalyticsResult } from '../types.js';

/** Formatea un valor monetario con separador de miles y 2 decimales. */
function money(value: number | string | null | undefined): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Claves de dimensiones agrupables (alineadas con la lista blanca del backend). */
const DIMENSION_KEYS = [
  'genre', 'subgenre', 'artist', 'label', 'origin', 'type',
  'media_condition', 'vinyl_color', 'discogs_format', 'location', 'decade', 'year',
] as const;

/** Claves de métricas (alineadas con la lista blanca del backend). */
const METRIC_KEYS = [
  'count_records', 'total_vinyl', 'distinct_artists',
  'sum_value', 'avg_value', 'sum_discogs_mid', 'avg_discogs_mid',
] as const;

/** Métricas monetarias: se formatean como dinero en la tabla de resultados. */
const VALUE_METRICS = new Set(['sum_value', 'avg_value', 'sum_discogs_mid', 'avg_discogs_mid']);

export function AnalyticsView() {
  const { t } = useTranslation();
  const api = useApi();
  // Etiqueta legible de una columna (dimensión o métrica) por su clave.
  const colLabel = (key: string): string => {
    if ((DIMENSION_KEYS as readonly string[]).includes(key)) return t(`analytics.dim.${key}`);
    if ((METRIC_KEYS as readonly string[]).includes(key)) return t(`analytics.metric.${key}`);
    return key;
  };

  // Resumen de valor de la colección (se carga al entrar, sin construir vista):
  //  - totales: una sola fila con el valor REAL y el valor medio de Discogs.
  //  - por género: valor REAL agrupado por género (top 50).
  const valueSummary = useAsync(
    async () => {
      const [totals, byGenre] = await Promise.all([
        api.analytics.query({ group_by: [], metrics: ['sum_value', 'sum_discogs_mid'] }),
        api.analytics.query({ group_by: ['genre'], metrics: ['sum_value'], limit: 50 }),
      ]);
      const row = totals.rows[0] ?? {};
      return {
        totalReal: Number(row.sum_value ?? 0),
        totalDiscogs: Number(row.sum_discogs_mid ?? 0),
        byGenre: byGenre.rows
          .map((r) => ({ genre: (r.genre as string) ?? t('analytics.noGenre'), value: Number(r.sum_value ?? 0) }))
          .filter((g) => g.value > 0),
      };
    },
    [],
  );

  const [groupBy, setGroupBy] = useState<string[]>(['genre']);
  const [metrics, setMetrics] = useState<string[]>(['count_records']);
  const [yearBasis, setYearBasis] = useState<'edition' | 'original'>('edition');
  const [limit, setLimit] = useState(20);
  const [genreFilter, setGenreFilter] = useState('');
  const [yearMin, setYearMin] = useState('');
  const [yearMax, setYearMax] = useState('');
  const [result, setResult] = useState<AnalyticsResult | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(list: string[], setList: (v: string[]) => void, key: string) {
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  }

  function buildFilters(): SearchCriteria {
    const f: SearchCriteria = {};
    if (genreFilter) f.genre = genreFilter;
    if (yearMin || yearMax) {
      f.edition_year = {
        ...(yearMin ? { min: Number(yearMin) } : {}),
        ...(yearMax ? { max: Number(yearMax) } : {}),
      };
    }
    return f;
  }

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const body: AnalyticsQueryBody = {
        group_by: groupBy,
        metrics,
        filters: buildFilters(),
        year_basis: yearBasis,
        limit,
      };
      const res = await api.analytics.query(body);
      setResult(res);
      setSortCol(res.metrics[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('analytics.errQuery'));
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo(
    () => (result ? [...result.group_by, ...result.metrics] : []),
    [result],
  );

  const sortedRows = useMemo(() => {
    if (!result) return [];
    const rows = [...result.rows];
    if (sortCol) {
      rows.sort((a, b) => {
        const av = a[sortCol];
        const bv = b[sortCol];
        const an = typeof av === 'number' ? av : Number(av);
        const bn = typeof bv === 'number' ? bv : Number(bv);
        const cmp =
          Number.isFinite(an) && Number.isFinite(bn)
            ? an - bn
            : String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [result, sortCol, sortDir]);

  // Gráfico: primera métrica por la primera dimensión.
  const chartMetric = result?.metrics[0];
  const chartDim = result?.group_by[0];
  const maxVal = useMemo(() => {
    if (!result || !chartMetric) return 0;
    return Math.max(0, ...result.rows.map((r) => Number(r[chartMetric] ?? 0)));
  }, [result, chartMetric]);

  function exportCsv() {
    if (!result) return;
    const header = columns.join(',');
    const lines = sortedRows.map((r) => columns.map((c) => csvCell(r[c])).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analitica.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('analytics.pageTitle')}
        subtitle={t('analytics.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
      <AlertMessage message={error} />

      {/* Resumen de valor de la colección (valor real vs. valor medio Discogs). */}
      <Card title={t('analytics.collectionValue')} icon="payments">
        <AsyncBoundary state={valueSummary}>
          {(v) => (
            <div className="flex flex-col gap-space-md">
              {/* Totales. */}
              <div className="grid grid-cols-2 gap-space-sm">
                <div className="surface-rack-card !p-space-md flex flex-col gap-space-xs">
                  <span className="flex items-center gap-space-xs section-label text-on-surface-variant">
                    <Icon name="savings" className="text-[16px] text-primary" /> {t('analytics.realValueEntered')}
                  </span>
                  <span className="font-headline-md text-headline-md text-vacuum-glow">{money(v.totalReal)}</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">{t('analytics.realValueDesc')}</span>
                </div>
                <div className="surface-rack-card !p-space-md flex flex-col gap-space-xs">
                  <span className="flex items-center gap-space-xs section-label text-on-surface-variant">
                    <Icon name="trending_up" className="text-[16px] text-primary" /> {t('analytics.discogsMidValue')}
                  </span>
                  <span className="font-headline-md text-headline-md text-goldmine-mint">{money(v.totalDiscogs)}</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">{t('analytics.discogsMidDesc')}</span>
                </div>
              </div>

              {/* Valor por género (valor real). */}
              {v.byGenre.length > 0 && (
                <div className="flex flex-col gap-space-xs">
                  <span className="section-label text-vacuum-glow">{t('analytics.valueByGenre')}</span>
                  {(() => {
                    const max = Math.max(...v.byGenre.map((g) => g.value));
                    return v.byGenre.map((g) => (
                      <div key={g.genre} className="grid grid-cols-[110px_1fr_auto] gap-space-xs items-center">
                        <span className="font-body-sm text-body-sm text-on-surface-variant truncate" title={g.genre}>
                          {g.genre}
                        </span>
                        <div className="meter-track !h-4">
                          <div
                            className="h-full bg-primary-container rounded-full"
                            style={{ width: `${max > 0 ? (g.value / max) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="font-code-catalog text-body-sm text-primary text-right whitespace-nowrap">
                          {money(g.value)}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}
        </AsyncBoundary>
      </Card>

      <Card title={t('analytics.viewBuilder')} icon="dashboard_customize">
        <span className="section-label text-vacuum-glow">{t('analytics.groupBy')}</span>
        <div className="flex flex-wrap gap-space-xs mt-space-xs mb-space-sm">
          {DIMENSION_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip ${groupBy.includes(key) ? 'chip-active' : ''}`}
              onClick={() => toggle(groupBy, setGroupBy, key)}
            >
              {groupBy.includes(key) && <Icon name="check" className="text-[14px]" />} {t(`analytics.dim.${key}`)}
            </button>
          ))}
        </div>

        <span className="section-label text-vacuum-glow">{t('analytics.metrics')}</span>
        <div className="flex flex-wrap gap-space-xs mt-space-xs mb-space-sm">
          {METRIC_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip ${metrics.includes(key) ? 'chip-active' : ''}`}
              onClick={() => toggle(metrics, setMetrics, key)}
            >
              {metrics.includes(key) && <Icon name="check" className="text-[14px]" />} {t(`analytics.metric.${key}`)}
            </button>
          ))}
        </div>

        <span className="section-label text-vacuum-glow">{t('analytics.filters')}</span>
        <div className="grid grid-cols-2 gap-space-sm mt-space-xs">
          <Field label={t('analytics.filterGenre')}><input className="field-input" value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)} /></Field>
          <Field label={t('analytics.yearBasis')}>
            <select className="field-input" value={yearBasis} onChange={(e) => setYearBasis(e.target.value as 'edition' | 'original')}>
              <option value="edition">{t('analytics.yearBasisEdition')}</option>
              <option value="original">{t('analytics.yearBasisOriginal')}</option>
            </select>
          </Field>
          <Field label={t('analytics.yearFrom')}><input className="field-input" type="number" value={yearMin} onChange={(e) => setYearMin(e.target.value)} /></Field>
          <Field label={t('analytics.yearTo')}><input className="field-input" type="number" value={yearMax} onChange={(e) => setYearMax(e.target.value)} /></Field>
          <Field label={t('analytics.topN')}><input className="field-input" type="number" min={1} value={limit} onChange={(e) => setLimit(Number(e.target.value))} /></Field>
        </div>

        <button className="btn-primary w-full mt-space-md" type="button" disabled={busy || groupBy.length === 0} onClick={() => void run()}>
          <Icon name="analytics" className="text-[18px]" />
          {busy ? t('analytics.calculating') : t('analytics.calculate')}
        </button>
      </Card>

      {result && (
        <Card
          title={t('analytics.resultTitle', { count: result.rows.length })}
          icon="monitoring"
          action={
            <button className="btn-ghost btn-sm" type="button" onClick={exportCsv}>
              <Icon name="download" className="text-[16px]" /> CSV
            </button>
          }
        >
          {chartMetric && chartDim && maxVal > 0 && (
            <div className="flex flex-col gap-space-xs mb-space-md">
              {sortedRows.slice(0, 12).map((r, i) => {
                const val = Number(r[chartMetric] ?? 0);
                return (
                  <div key={i} className="grid grid-cols-[110px_1fr_48px] gap-space-xs items-center">
                    <span className="font-body-sm text-body-sm text-on-surface-variant truncate" title={String(r[chartDim] ?? '')}>
                      {String(r[chartDim] ?? '—')}
                    </span>
                    <div className="meter-track !h-4">
                      <div className="h-full bg-primary-container rounded-full" style={{ width: `${(val / maxVal) * 100}%` }} />
                    </div>
                    <span className="font-code-catalog text-body-sm text-primary text-right">{val}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full font-code-catalog text-body-sm">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c}
                      className="text-left py-space-xs px-space-xs text-on-surface-variant uppercase cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        if (sortCol === c) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        else { setSortCol(c); setSortDir('desc'); }
                      }}
                    >
                      {colLabel(c)}{sortCol === c ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-on-surface">
                {sortedRows.map((r, i) => (
                  <tr key={i} className="border-t border-border-subtle">
                    {columns.map((c) => (
                      <td key={c} className="py-space-xs px-space-xs whitespace-nowrap">
                        {VALUE_METRICS.has(c) ? money(r[c]) : format(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-space-xs">
      <label className="field-label">{label}</label>
      <div className="field-slot">{children}</div>
    </div>
  );
}

function format(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return v;
}

function csvCell(v: string | number | null | undefined): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
